import { Package, User, Clock, MapPin, Check, UserCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getTaskStyle, getDeadlineStatus } from '@/lib/priority';
import type { Task } from '@/api/tasks';

interface TaskCardProps {
  task: Task;
  onComplete: (id: string) => void;
  onSelect?: (task: Task) => void;
  isCompleting?: boolean;
}

function formatDeadline(deadline: string): string {
  const date = new Date(deadline);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const taskDate = new Date(deadline);
  taskDate.setHours(0, 0, 0, 0);

  if (taskDate.getTime() === today.getTime()) {
    return 'Сегодня';
  }
  if (taskDate.getTime() === tomorrow.getTime()) {
    return 'Завтра';
  }

  return date.toLocaleDateString('ru', {
    day: 'numeric',
    month: 'short',
  });
}

export function TaskCard({ task, onComplete, onSelect, isCompleting }: TaskCardProps) {
  const deadlineStatus = getDeadlineStatus(task.deadline);
  const isOverdue = deadlineStatus === 'overdue';

  const style = getTaskStyle({
    type: task.type,
    deadline: task.deadline,
    isCompleted: task.status === 'COMPLETED',
    isCancelled: task.status === 'CANCELLED',
  });
  const Icon = task.type === 'PERSONAL' ? User : Package;

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking the complete button
    if ((e.target as HTMLElement).closest('button')) return;
    onSelect?.(task);
  };

  return (
    <div
      className={cn(
        'p-3 rounded-lg border transition-all',
        style.bg,
        style.border,
        style.text,
        onSelect && 'cursor-pointer hover:shadow-md active:scale-[0.99]'
      )}
      onClick={handleCardClick}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={cn(
            'p-2 rounded-lg flex-shrink-0',
            task.type === 'PERSONAL'
              ? 'bg-cyan-100 text-cyan-700'
              : 'bg-amber-100 text-amber-700'
          )}
        >
          <Icon className="h-4 w-4" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3
            className={cn(
              'font-medium text-sm leading-tight',
              style.text,
              style.strikethrough && 'line-through'
            )}
          >
            {task.title}
          </h3>

          {/* Description */}
          {task.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
              {task.description}
            </p>
          )}

          {/* Meta row */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {/* Client */}
            {task.client && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground dark:text-gray-900">
                <MapPin className="h-3 w-3" />
                <span className="truncate max-w-[100px]">{task.client.name}</span>
              </div>
            )}

            {/* Employee (if not own task) */}
            {task.employee && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground dark:text-gray-900">
                <UserCircle className="h-3 w-3" />
                <span className="truncate max-w-[80px]">
                  {task.employee.lastName}
                </span>
              </div>
            )}

            {/* Deadline */}
            {task.deadline && (
              <Badge
                variant={isOverdue ? 'destructive' : 'outline'}
                className="text-[10px] px-1.5 dark:bg-transparent dark:text-gray-900"
              >
                <Clock className="h-3 w-3 mr-0.5" />
                {formatDeadline(task.deadline)}
              </Badge>
            )}

            {/* Type badge - only for list view, not needed in client detail */}
            {!task.client && (
              <Badge variant="secondary" className="text-[10px] px-1.5 dark:bg-transparent dark:text-gray-900">
                {task.type === 'PERSONAL' ? 'Личное' : 'Внешнее'}
              </Badge>
            )}
          </div>
        </div>

        {/* Complete button */}
        {task.status === 'PENDING' && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onComplete(task.id);
            }}
            disabled={isCompleting}
          >
            <Check className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
